import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Plus, Trash2, CheckCircle2, Circle, CalendarDays, ListTodo } from "lucide-react";
import { toast } from "sonner";

function toKey(d) {
  return d.toISOString().slice(0, 10);
}

export default function RightPanel() {
  const [selected, setSelected] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [todos, setTodos] = useState([]);
  const [eventTitle, setEventTitle] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [todoTitle, setTodoTitle] = useState("");

  const loadEvents = () => api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  const loadTodos = () => api.get("/todos").then((r) => setTodos(r.data)).catch(() => {});
  useEffect(() => { loadEvents(); loadTodos(); }, []);

  const key = toKey(selected);
  const dayEvents = events.filter((e) => e.date === key);
  const eventDays = events.map((e) => new Date(e.date + "T00:00:00"));

  const addEvent = async (e) => {
    e.preventDefault();
    if (!eventTitle.trim()) return;
    const { data } = await api.post("/events", { title: eventTitle, date: key, time: eventTime || null });
    setEvents((prev) => [...prev, data]);
    setEventTitle(""); setEventTime("");
    toast.success("Event added");
  };
  const removeEvent = async (id) => {
    await api.delete(`/events/${id}`); setEvents((p) => p.filter((e) => e.id !== id));
  };

  const addTodo = async (e) => {
    e.preventDefault();
    if (!todoTitle.trim()) return;
    const { data } = await api.post("/todos", { title: todoTitle });
    setTodos((prev) => [data, ...prev]);
    setTodoTitle("");
  };
  const toggleTodo = async (t) => {
    await api.patch(`/todos/${t.id}`, { done: !t.done });
    setTodos((prev) => prev.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)));
  };
  const removeTodo = async (id) => {
    await api.delete(`/todos/${id}`); setTodos((p) => p.filter((t) => t.id !== id));
  };

  const inputCls =
    "min-w-0 bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00]";

  return (
    <div className="h-full overflow-y-auto p-5 space-y-6">
      {/* Calendar */}
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3">
          <CalendarDays size={14} /> Calendar
        </div>
        <div className="bg-[#15151d] border border-[#2a3040] rounded-lg p-2">
          <Calendar mode="single" selected={selected} onSelect={(d) => d && setSelected(d)}
            modifiers={{ hasEvent: eventDays }}
            modifiersClassNames={{ hasEvent: "border border-[#FF5A00] rounded-md" }}
            classNames={{
              day_today: "bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-md",
              day_selected: "bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-md hover:bg-[#FF7A33] focus:bg-[#FF5A00]",
            }}
            className="text-white" />
        </div>

        <form onSubmit={addEvent} className="mt-3 space-y-2" data-testid="event-form">
          <div className="text-xs text-[#9ca3af]">{selected.toDateString()}</div>
          <input data-testid="event-title-input" className={inputCls + " w-full"} placeholder="Add event…"
            value={eventTitle} onChange={(e) => setEventTitle(e.target.value)} />
          <div className="flex gap-2">
            <input data-testid="event-time-input" type="time"
              className="flex-1 min-w-0 bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF5A00]"
              value={eventTime} onChange={(e) => setEventTime(e.target.value)} />
            <button data-testid="event-add-button" type="submit"
              className="shrink-0 w-9 h-9 rounded-lg bg-[#FF5A00] text-[#0a0a0f] flex items-center justify-center hover:bg-[#FF7A33] transition-colors">
              <Plus size={16} />
            </button>
          </div>
        </form>

        <div className="mt-3 space-y-2">
          {dayEvents.length === 0 ? (
            <p className="text-xs text-[#6b7280]">No events for this day.</p>
          ) : dayEvents.map((ev) => (
            <div key={ev.id} data-testid={`event-item-${ev.id}`}
              className="flex items-center gap-2 bg-[#1a1a24] border border-[#2a3040] rounded-lg px-3 py-2 group">
              {ev.time && <span className="text-xs font-bold text-[#FF5A00] shrink-0">{ev.time}</span>}
              <span className="text-sm text-white flex-1 truncate">{ev.title}</span>
              <button onClick={() => removeEvent(ev.id)} className="text-[#6b7280] hover:text-[#f87171] opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Todos */}
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3">
          <ListTodo size={14} /> Today's To-Do's
        </div>
        <form onSubmit={addTodo} className="flex gap-2 mb-3" data-testid="todo-form">
          <input data-testid="todo-input" className={inputCls + " flex-1"} placeholder="What needs doing?"
            value={todoTitle} onChange={(e) => setTodoTitle(e.target.value)} />
          <button data-testid="todo-add-button" type="submit"
            className="shrink-0 w-9 h-9 rounded-lg bg-[#FF5A00] text-[#0a0a0f] flex items-center justify-center hover:bg-[#FF7A33] transition-colors">
            <Plus size={16} />
          </button>
        </form>
        <ol className="space-y-2">
          {todos.length === 0 ? (
            <p className="text-xs text-[#6b7280]">All clear. Add your first task.</p>
          ) : todos.map((t, idx) => (
            <li key={t.id} data-testid={`todo-item-${t.id}`}
              className="flex items-center gap-2 bg-[#1a1a24] border border-[#2a3040] rounded-lg px-3 py-2 group">
              <span className="text-xs font-bold text-[#FF5A00] w-4 shrink-0 text-right">{idx + 1}.</span>
              <button data-testid={`todo-toggle-${t.id}`} onClick={() => toggleTodo(t)}
                className={t.done ? "text-[#FF5A00]" : "text-[#6b7280] hover:text-white"}>
                {t.done ? <CheckCircle2 size={18} /> : <Circle size={18} />}
              </button>
              <span className={`text-sm flex-1 ${t.done ? "line-through text-[#6b7280]" : "text-white"}`}>{t.title}</span>
              <button onClick={() => removeTodo(t.id)} className="text-[#6b7280] hover:text-[#f87171] opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
